import {
  always,
  assoc,
  assocPath,
  compose,
  defaultTo,
  dissoc,
  equals,
  filter,
  ifElse,
  isEmpty,
  length,
  not,
  path,
  pipe,
  propEq,
  propOr,
  reject,
  sort,
  when
} from 'ramda';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import {
  Button,
  Icon,
  PageTitle,
  Tabs,
  Tooltip
} from '@fasstech/middle-design-system';
import ButtonContainer from '@@viewCommonComponents/forms/ButtonContainer';
import BeneficiariesRefund from './components/BeneficiariesRefund';
import ConfirmationModal from '@@viewCommonComponents/displayer/ConfirmationModal';
import useSouscriptionPaymentRefundForm from './hooks/useSouscriptionPaymentRefundForm';
import { SignatureModeEnum } from '@fasstech/oip-core/entities/Signature/enums';
import React from 'react';
import {
  FormLayout,
  PageLayout
} from '@@viewCommonComponents/forms/Layouts.jsx';
import TabsPagination from '@@viewCommonComponents/TabsPagination.jsx';
import { useTabs } from '@@viewCommonComponents/hooks/useTabs.js';
import {
  isNilOrEmpty,
  isNotNilOrEmpty
} from '../../../server/helpers/index.mjs';
import CustomerPaymentRefund from './components/CustomerPaymentRefund.jsx';
import extraStyle from './extra/extraStyle';
import { useAnalytics } from '@@app/context/analytics-context.js';

const SouscriptionPaymentRefundForm = ({
  getMiddleText,
  getMiddleBoolean,
  middleFeatures,
  project,
  phoneIndicatives
}) => {
  const {
    PAIEMENTETREMBOURSEMENT_JOUR_PRELEVEMENT_EST_NECESSAIRE,
    PAIEMENTETREMBOURSEMENT_JOUR_PRELEVEMENT_VALEUR,
    PAIEMENTETREMBOURSEMENT_JOUR_PRELEVEMENT_DEFAULT,
    PAIEMENTETREMBOURSEMENT_FREQUENCE_PAIEMENT_PRELEVEMENT_EST_NECESSAIRE,
    PAIEMENTETREMBOURSEMENT_FREQUENCE_PAIEMENT_PRELEVEMENT_VALEUR,
    PAIEMENTETREMBOURSEMENT_FREQUENCE_PAIEMENT_PRELEVEMENT_DEFAULT,
    PAIEMENTETREMBOURSEMENT_FREQUENCE_PAIEMENT_VIREMENT_EST_NECESSAIRE,
    PAIEMENTETREMBOURSEMENT_FREQUENCE_PAIEMENT_VIREMENT_VALEUR,
    PAIEMENTETREMBOURSEMENT_FREQUENCE_PAIEMENT_VIREMENT_DEFAULT,
    PAIEMENTETREMBOURSEMENT_TYPE_PAIEMENT_PRELEVEMENT_AUTORISE,
    PAIEMENTETREMBOURSEMENT_TYPE_PAIEMENT_VIREMENT_AUTORISE
  } = middleFeatures;

  const projectPaymentType = path(['bankAccounts', 0, 'type'], project);

  const isVirementType = equals('VIREMENT', projectPaymentType);

  const shouldUseVirementDefaults =
    isVirementType ||
    (!projectPaymentType &&
      !PAIEMENTETREMBOURSEMENT_TYPE_PAIEMENT_PRELEVEMENT_AUTORISE &&
      PAIEMENTETREMBOURSEMENT_TYPE_PAIEMENT_VIREMENT_AUTORISE);

  const frequencyIsRequired = shouldUseVirementDefaults
    ? PAIEMENTETREMBOURSEMENT_FREQUENCE_PAIEMENT_VIREMENT_EST_NECESSAIRE
    : PAIEMENTETREMBOURSEMENT_FREQUENCE_PAIEMENT_PRELEVEMENT_EST_NECESSAIRE;

  const frequencyValuesTable = shouldUseVirementDefaults
    ? PAIEMENTETREMBOURSEMENT_FREQUENCE_PAIEMENT_VIREMENT_VALEUR
    : PAIEMENTETREMBOURSEMENT_FREQUENCE_PAIEMENT_PRELEVEMENT_VALEUR;

  const frequencyDefault = shouldUseVirementDefaults
    ? PAIEMENTETREMBOURSEMENT_FREQUENCE_PAIEMENT_VIREMENT_DEFAULT
    : PAIEMENTETREMBOURSEMENT_FREQUENCE_PAIEMENT_PRELEVEMENT_DEFAULT;

  const defaultDebitFrequency = when(
    () => equals(true, frequencyIsRequired),
    compose(
      defaultTo(path(['0', 'CODE'])),
      always(frequencyDefault),
      filter(propEq('AUTORISE', 'true'))
    )
  )(frequencyValuesTable);

  const defaultDebitDay = ifElse(
    () => equals(true, PAIEMENTETREMBOURSEMENT_JOUR_PRELEVEMENT_EST_NECESSAIRE),
    compose(
      defaultTo(path(['0', 'CODE'])),
      always(PAIEMENTETREMBOURSEMENT_JOUR_PRELEVEMENT_DEFAULT),
      filter(propEq('AUTORISE', 'true'))
    ),
    always(null)
  )(PAIEMENTETREMBOURSEMENT_JOUR_PRELEVEMENT_VALEUR);

  const customer = propOr([], 'customer')(project);

  const granteeBankAccounts = compose(
    sort((a) => (isNilOrEmpty(a.bankAccounts) ? -1 : 1)),
    reject((e) => isNilOrEmpty(e.bankAccounts)),
    propOr([], 'grantees')
  )(project);
  const form = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: compose(
      assoc('grantees', granteeBankAccounts),
      when(
        () =>
          isNilOrEmpty(path(['bankAccounts', 0, 'debitFrequency'], project)),
        assocPath(['bankAccounts', 0, 'debitFrequency'], defaultDebitFrequency)
      ),
      when(
        () => isNilOrEmpty(path(['bankAccounts', 0, 'debitDay'], project)),
        assocPath(['bankAccounts', 0, 'debitDay'], defaultDebitDay)
      ),
      assocPath(['signature', 'mode'], SignatureModeEnum.ELECTRONIC),
      assoc(
        'radioDisplayRefundInformations',
        isNotNilOrEmpty(path(['customer', 'bankAccounts'], project))
      ),
      when(
        () => isNotNilOrEmpty(path(['contacts', 0, 'bankAccounts'], project)),
        compose(
          dissoc('contacts'),
          assocPath(
            ['bankAccounts', 0],
            path(['contacts', 0, 'bankAccounts', 0], project)
          ),
          assoc('bankAccounts', [])
        )
      ),
      assoc(
        'thirdPartyPayer',
        isNotNilOrEmpty(path(['contacts', 0, 'bankAccounts'], project))
      )
    )(project)
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'grantees',
    keyName: 'uuid'
  });

  const customerGrantees = compose(reject((e) => isEmpty(e.bankAccounts)))([
    customer,
    ...fields
  ]);

  const {
    onSubmit,
    isValid,
    displayConfirmationModal,
    setDisplayConfirmationModal,
    $onPrevClick,
    paymentDayOptions,
    paymentFrequencyOptions,
    paymentDayIsVisible
  } = useSouscriptionPaymentRefundForm({
    middleFeatures,
    form
  });

  const {
    tabs,
    setTabs,
    updateVisibility,
    isButtonDisabled,
    setSelectedGrantee,
    displayTabsPagination,
    setManualTrigger
  } = useTabs({
    grantees: customerGrantees,
    defaultMaxVisibleTabs: 5 + length(propOr([], 'grantees', project)),
    getMiddleText
  });

  const ManualSignatureButton = () => {
    const { trackButtonClick } = useAnalytics();

    return (
      <>
        {getMiddleBoolean('PROCESSDEVENTE_SIGNATURE_ELECTRONIQUE_DISPONIBLE') &&
          getMiddleBoolean('PROCESSDEVENTE_SIGNATURE_MANUELLE_DISPONIBLE') && (
          <Button
            className="font-bold w-full whitespace-nowrap py-[7px]!"
            data-testid="MANUAL_SIGNATURE_BUTTON"
            disabled={not(isValid)}
            variant={extraStyle.variant}
            size={'small'}
            onClick={() => {
              form.setValue('signature.mode', SignatureModeEnum.MANUAL);
              trackButtonClick({
                code: 'CONTINUE',
                label: getMiddleText(
                  'PAIEMENTETREMBOURSEMENT_MANUAL_SIGNATURE_BUTTON_LABEL'
                )
              });
            }}
            htmlType="submit"
          >
            {getMiddleText(
              'PAIEMENTETREMBOURSEMENT_MANUAL_SIGNATURE_BUTTON_LABEL'
            )}
          </Button>
        )}
      </>
    );
  };

  const submit = form.handleSubmit(onSubmit);
  const closeConfirmationModal = () => setDisplayConfirmationModal(false);

  return (
    <FormProvider {...form}>
      <form className={'w-full'} onSubmit={submit}>
        <PageLayout>
          <Icon name={'circleWallet'} size={70} />
          <PageTitle>
            {getMiddleText('PAIEMENTETREMBOURSEMENT_VIEW_TITLE')}
          </PageTitle>
          <FormLayout>
            <div className={'flex flex-col gap-4'}>
              <TabsPagination
                updateVisibility={updateVisibility}
                isButtonDisabled={isButtonDisabled}
                show={displayTabsPagination}
              >
                <Tabs
                  items={tabs.tabs}
                  selected={tabs.selectedTab}
                  onClick={(index) => {
                    setTabs({
                      selectedTab: index,
                      tabs: tabs.tabs
                    });
                    const grantee = fields[index];
                    if (grantee) {
                      setSelectedGrantee(grantee);
                    }
                  }}
                  isDisplayed={tabs.tabs.length > 1}
                />
              </TabsPagination>
            </div>

            <CustomerPaymentRefund
              selectedTab={tabs.selectedTab}
              getMiddleText={getMiddleText}
              getMiddleBoolean={getMiddleBoolean}
              paymentDayOptions={paymentDayOptions}
              paymentFrequencyOptions={paymentFrequencyOptions}
              paymentDayIsVisible={paymentDayIsVisible}
            />

            <BeneficiariesRefund
              selectedTab={tabs.selectedTab}
              getMiddleText={getMiddleText}
              getMiddleBoolean={getMiddleBoolean}
              grantees={propOr([], 'grantees', project)}
              fields={fields}
              form={form}
              setTabs={setTabs}
              append={append}
              remove={remove}
              setManualTrigger={setManualTrigger}
            />

            <ConfirmationModal
              isDisplayed={displayConfirmationModal}
              confirm={pipe(closeConfirmationModal, submit)}
              close={closeConfirmationModal}
              getMiddleText={getMiddleText}
              phoneIndicatives={phoneIndicatives}
              form={form}
            />
          </FormLayout>
        </PageLayout>
        <ButtonContainer
          $onPreviousButtonClick={$onPrevClick}
          $onNextButtonClick={() =>
            form.setValue(
              'signature.mode',
              getMiddleBoolean(
                'PROCESSDEVENTE_SIGNATURE_ELECTRONIQUE_DISPONIBLE'
              )
                ? SignatureModeEnum.ELECTRONIC
                : SignatureModeEnum.MANUAL
            )
          }
          nextButtonLabel={
            getMiddleBoolean('PROCESSDEVENTE_SIGNATURE_ELECTRONIQUE_DISPONIBLE')
              ? getMiddleText(
                'PAIEMENTETREMBOURSEMENT_ELECTRONIC_SIGNATURE_BUTTON_LABEL'
              )
              : getMiddleText(
                'PAIEMENTETREMBOURSEMENT_MANUAL_SIGNATURE_BUTTON_LABEL'
              )
          }
          getMiddleText={getMiddleText}
          nextButtonIsDisabled={not(isValid)}
          isFormError={not(isValid)}
          isSubscribeStep
          fasterFileProcessingLabel={
            getMiddleBoolean('PROCESSDEVENTE_SIGNATURE_ELECTRONIQUE_DISPONIBLE')
              ? 'Traitement plus rapide des dossiers'
              : null
          }
          displayResumeButton
        >
          <div className={extraStyle.buttonStyle}>
            {not(isValid) ? (
              <Tooltip
                variant="centered"
                text={getMiddleText(
                  'GENERAL_COMMON_TEXTS_TAB_ACTION_ERROR_WARNING_DESCRIPTION'
                )}
              >
                <ManualSignatureButton isValid={isValid} />
              </Tooltip>
            ) : (
              <ManualSignatureButton isValid={isValid} />
            )}
          </div>
        </ButtonContainer>
      </form>
    </FormProvider>
  );
};

export default SouscriptionPaymentRefundForm;
